import type { ComposerChannel } from '~publish/components/Composer/store/hooks/useChannels'

/**
 * Map Hootsuite's "Social Account" labels to Buffer service identifiers.
 * The `type` field distinguishes e.g. LinkedIn personal vs company page.
 */
const PLATFORM_MAP: Record<string, { service: string; type?: string }> = {
  twitter: { service: 'twitter' },
  'x (twitter)': { service: 'twitter' },
  linkedin: { service: 'linkedin', type: 'profile' },
  'linkedin company': { service: 'linkedin', type: 'page' },
  'instagram business': { service: 'instagram' },
  instagram: { service: 'instagram' },
  threads: { service: 'threads' },
  pinterest: { service: 'pinterest' },
  facebook: { service: 'facebook' },
  'facebook page': { service: 'facebook', type: 'page' },
  tiktok: { service: 'tiktok' },
}

type ChannelMatch = {
  channelId: string
  confidence: 'exact' | 'partial' | 'none'
}

/**
 * Attempt to auto-match a Hootsuite source channel to a connected Buffer channel.
 *
 * Matching strategy:
 * 1. Match platform type (required)
 * 2. Match account name (fuzzy — case-insensitive, trim whitespace, strip leading @)
 */
export function findBestChannelMatch(
  sourcePlatform: string,
  sourceAccountName: string,
  bufferChannels: ComposerChannel[],
): ChannelMatch | null {
  const platformKey = sourcePlatform.trim().toLowerCase()
  const mapping = PLATFORM_MAP[platformKey]

  if (!mapping) return null

  // Filter to channels matching the platform
  const platformMatches = bufferChannels.filter((ch) => {
    if (ch.service !== mapping.service) return false
    if (mapping.type && ch.type !== mapping.type) return false
    return true
  })

  if (platformMatches.length === 0) return null

  // Try exact name match first
  const normalizedSource = normalizeAccountName(sourceAccountName)
  for (const ch of platformMatches) {
    const normalizedName = normalizeAccountName(ch.name ?? '')
    const normalizedDisplayName = normalizeAccountName(ch.displayName ?? '')

    if (
      normalizedSource === normalizedName ||
      normalizedSource === normalizedDisplayName
    ) {
      return { channelId: ch.id, confidence: 'exact' }
    }
  }

  // Try partial/contains match
  for (const ch of platformMatches) {
    const normalizedName = normalizeAccountName(ch.name ?? '')
    const normalizedDisplayName = normalizeAccountName(ch.displayName ?? '')

    if (
      partialContains(normalizedName, normalizedSource) ||
      partialContains(normalizedDisplayName, normalizedSource)
    ) {
      return { channelId: ch.id, confidence: 'partial' }
    }
  }

  // If there's only one channel for this platform, use it as a fallback
  if (platformMatches.length === 1) {
    return { channelId: platformMatches[0].id, confidence: 'partial' }
  }

  return null
}

function normalizeAccountName(name: string): string {
  return name.trim().toLowerCase().replace(/^@/, '')
}

// `s.includes('')` is always true for any string `s`, so if either operand
// is empty the symmetric contains-check below would always return a false
// positive — e.g. a Buffer channel with empty name/displayName would match
// any source account. Guard by short-circuiting when either side is empty.
function partialContains(channelName: string, source: string): boolean {
  if (!channelName || !source) return false
  return channelName.includes(source) || source.includes(channelName)
}

/**
 * Check whether a Hootsuite platform string maps to a known Buffer service.
 */
export function isKnownPlatform(sourcePlatform: string): boolean {
  return sourcePlatform.trim().toLowerCase() in PLATFORM_MAP
}
